/**
 * Mega Menu Configuration
 *
 * Defines the 7-domain menu structure for the mega menu overlay.
 * Each domain contains items with paths, icons, and optional badge keys.
 */

import type { ReactNode } from 'react';

export interface MegaMenuItem {
  label: string;
  path: string;
  icon?: string;
  /** Key to look up badge count from initialState */
  badgeKey?: string;
  /** ACL module/action for permission check */
  acl?: {
    module: string;
    action: string;
  };
  /** When true, only visible to superadmin operators (no vendorId) */
  superadminOnly?: boolean;
  /** Whether this is a parent item that expands to show children */
  children?: MegaMenuItem[];
}

export interface MegaMenuDomain {
  id: string;
  title: string;
  icon: string;
  items: MegaMenuItem[];
}

export interface MegaMenuRow {
  domains: MegaMenuDomain[];
}

/**
 * Work domain - All pending/actionable items
 * Organized into logical groups: Fiat Operations, Digital Assets, Cards, Onboarding
 */
const workDomain: MegaMenuDomain = {
  id: 'work',
  title: 'Work',
  icon: 'ClockCircleOutlined',
  items: [
    {
      label: 'Fiat Operations',
      path: '/pending-instructions/payments',
      icon: 'BankOutlined',
      children: [
        {
          label: 'Pending Payments',
          path: '/pending-instructions/payments',
          icon: 'DollarOutlined',
          badgeKey: 'payments',
          acl: { module: 'PENDING_PAYMENT', action: 'VIEW:PENDING_PAYMENT' },
        },
        {
          label: 'Pending Instructions',
          path: '/pending-instructions/instructions',
          icon: 'FileTextOutlined',
          badgeKey: 'instructions',
          acl: { module: 'ALL_INSTRUCTION', action: 'VIEW:PENDING_INSTRUCTION' },
        },
        {
          label: 'CRA Forms',
          path: '/pending-instructions/cra-form',
          icon: 'FormOutlined',
          badgeKey: 'craFormInstructions',
          acl: { module: 'USER', action: 'VIEW:USER_PENDING_CRA_FORM' },
        },
      ],
    },
    {
      label: 'Digital Assets',
      path: '/digital-custody/deposits/pending',
      icon: 'BlockOutlined',
      children: [
        {
          label: 'Deposit Exceptions',
          path: '/digital-custody/deposits/pending',
          icon: 'BlockOutlined',
          badgeKey: 'digitalDepositPending',
          acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_DEPOSIT_PENDING' },
        },
        {
          label: 'Rejected Deposits',
          path: '/digital-custody/deposits',
          icon: 'ClockCircleOutlined',
          badgeKey: 'digitalDepositRejected',
          acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_DEPOSIT' },
        },
        {
          label: 'Withdrawal Requests',
          path: '/digital-custody/withdraws',
          icon: 'SwapOutlined',
          badgeKey: 'digitalWithdrawPending',
          acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_WITHDRAW' },
        },
      ],
    },
    {
      label: 'Cards',
      path: '/cms/card-application',
      icon: 'CreditCardOutlined',
      children: [
        {
          label: 'Card Applications',
          path: '/cms/card-application',
          icon: 'CreditCardOutlined',
          badgeKey: 'cardApplicationCount',
          acl: { module: 'CMS', action: 'VIEW:CMS_CARD_APPLICATION' },
        },
        {
          label: 'Pending Embossed',
          path: '/cms/card-pending-embossed',
          icon: 'IdcardOutlined',
          badgeKey: 'cardPendingEmbossedCount',
          acl: { module: 'CMS', action: 'VIEW:CMS_PENDING_EMBOSSED' },
        },
      ],
    },
    {
      label: 'Onboarding',
      path: '/solution-application',
      icon: 'SolutionOutlined',
      children: [
        {
          label: 'Solution Applications',
          path: '/solution-application',
          icon: 'SolutionOutlined',
          badgeKey: 'solutionApplications',
          acl: { module: 'SOLUTION', action: 'VIEW:SOLUTION_APPLICATION' },
        },
      ],
    },
  ],
};

/**
 * Customers domain - Entity lookup
 */
const customersDomain: MegaMenuDomain = {
  id: 'customers',
  title: 'Customers',
  icon: 'UserOutlined',
  items: [
    {
      label: 'Users',
      path: '/users',
      children: [
        { label: 'User Lookup', path: '/users', acl: { module: 'USER', action: 'VIEW:USER' } },
        { label: 'Client Migrations', path: '/users/client-migrations', acl: { module: 'USER', action: 'VIEW:USER_MIGRATION_CAMPAIGN' } },
        { label: 'User Groups', path: '/user-groups', acl: { module: 'USER', action: 'VIEW:USER_GROUP' } },
        { label: 'Welcome Email Templates', path: '/welcome-email-templates', acl: { module: 'WELCOME_EMAIL_TEMPLATE', action: 'VIEW:WELCOME_EMAIL_TEMPLATE' } },
        { label: 'Invitation Codes', path: '/invitation-code', acl: { module: 'INVITATION_CODE', action: 'VIEW:INVITATION_CODE' } },
        { label: 'Relationship Managers', path: '/relationship-managers', acl: { module: 'RELATIONSHIP_MANAGER', action: 'VIEW:RELATIONSHIP_MANAGER' } },
      ],
    },
    {
      label: 'Accounts',
      path: '/client-account/wallet-accounts',
      children: [
        { label: 'Wallet Accounts', path: '/client-account/wallet-accounts', acl: { module: 'WALLET', action: 'VIEW:WALLET_ACCOUNTS' } },
        { label: 'Client Deposit Accounts', path: '/client-account/client-deposit-accounts', acl: { module: 'FIAT', action: 'VIEW:*' } },
        { label: 'OD Account', path: '/client-account/od-account', acl: { module: 'OD_ACCOUNT', action: 'VIEW:OD_ACCOUNT' } },
        { label: 'Bank Payout Whitelist', path: '/client-account/bank-payout-whitelist', acl: { module: 'BANK_ACCOUNTS', action: 'VIEW:BANK_ACCOUNTS' } },
      ],
    },
    {
      label: 'Documents',
      path: '/client-documents',
      children: [
        { label: 'Client Documents', path: '/client-documents', acl: { module: 'CLIENT_DOCUMENT', action: 'VIEW:CLIENT_DOCUMENT' } },
        { label: 'Document Templates', path: '/document-templates', acl: { module: 'PDF_TEMPLATE', action: 'VIEW:PDF_TEMPLATE' } },
      ],
    },
  ],
};

/**
 * Instructions domain - All money movement
 */
const instructionsDomain: MegaMenuDomain = {
  id: 'instructions',
  title: 'Instructions',
  icon: 'SwapOutlined',
  items: [
    {
      label: 'Search',
      path: '/instructions/all',
      children: [
        { label: 'All Instructions', path: '/instructions/all', acl: { module: 'ALL_INSTRUCTION', action: 'VIEW:ALL_INSTRUCTION' } },
        { label: 'Scheduled', path: '/instructions/scheduled', acl: { module: 'SCHEDULED_INSTRUCTION', action: 'VIEW:SCHEDULED_INSTRUCTION' } },
      ],
    },
    {
      label: 'Fiat',
      path: '/instructions/fiat-deposits',
      children: [
        { label: 'Fiat Deposits', path: '/instructions/fiat-deposits', acl: { module: 'FIAT', action: 'VIEW:FIAT_DEPOSIT' } },
        { label: 'Fiat Withdrawals', path: '/instructions/fiat-withdraws', acl: { module: 'FIAT', action: 'VIEW:FIAT_WITHDRAW' } },
        { label: 'Cash In/Out', path: '/instructions/cash-in-out', acl: { module: 'CASH_IN_OUT', action: 'VIEW:CASH_IN_OUT' } },
      ],
    },
    {
      label: 'Digital Assets',
      path: '/digital-custody/deposits',
      children: [
        { label: 'Digital Deposits', path: '/digital-custody/deposits', acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_DEPOSIT' } },
        { label: 'Digital Withdrawals', path: '/digital-custody/withdraws', acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_WITHDRAW' } },
      ],
    },
    {
      label: 'Trading',
      path: '/instructions/exchanges',
      children: [
        { label: 'Exchanges', path: '/instructions/exchanges', acl: { module: 'EXCHANGE', action: 'VIEW:EXCHANGE' } },
      ],
    },
    {
      label: 'Transfer & Payments',
      path: '/instructions/transfers',
      children: [
        { label: 'Account Transfers', path: '/instructions/transfers', acl: { module: 'TRANSFER', action: 'VIEW:TRANSFER' } },
        { label: 'Bill Payments', path: '/instructions/bill-payments', acl: { module: 'BILL_PAYMENT', action: 'VIEW:BILL_PAYMENT' } },
        { label: 'UnionPay Remittance', path: '/instructions/unionpay-remittance', acl: { module: 'UNIONPAY_REMITTANCE', action: 'VIEW:UNIONPAY_REMITTANCE' } },
        { label: 'Alipay Remittance', path: '/instructions/alipay-remittance', acl: { module: 'ALIPAY_REMITTANCE', action: 'VIEW:ALIPAY_REMITTANCE' } },
      ],
    },
  ],
};

/**
 * Cards & Credit domain - Card management and credit products
 */
const cardsDomain: MegaMenuDomain = {
  id: 'cards',
  title: 'Cards & Credit',
  icon: 'CreditCardOutlined',
  items: [
    {
      label: 'Cards',
      path: '/cms/cards',
      children: [
        { label: 'All Cards', path: '/cms/cards', acl: { module: 'CMS', action: 'VIEW:CMS_CARD' } },
        { label: 'Card Programs', path: '/card-programs', acl: { module: 'CMS', action: 'VIEW:CARD_PROGRAM' } },
        { label: 'Card Transactions', path: '/cms/transactions', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_TRANSACTIONS' } },
        { label: 'Card Factory', path: '/cms/card-factory', acl: { module: 'CMS', action: 'VIEW:CARD_FACTORY_BATCH' } },
        { label: 'Card Products V2', path: '/cms/card-products-v2', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_PRODUCT' } },
        { label: 'Card Renewal', path: '/cms/card-renewal', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_RENEWAL' } },
      ],
    },
    {
      label: 'Accounts',
      path: '/cms/card-account',
      children: [
        { label: 'Card Account', path: '/cms/card-account', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_ACCOUNT' } },
        { label: 'Card CUPD Balances', path: '/cms/card-cupd-balances', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_BALANCES' } },
        { label: 'Card Balances', path: '/cms/card-balances', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_BALANCES' } },
        { label: 'Card Repayment', path: '/cms/card-repayment', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_REPAYMENT' } },
        { label: 'Card Interest', path: '/cms/card-account-interest', acl: { module: 'ACCOUNTING', action: 'VIEW:CARD_INTEREST' } },
      ],
    },
    {
      label: 'KYC & Security',
      path: '/cms/card-kyc-status',
      children: [
        { label: 'Card KYC', path: '/cms/card-kyc-status', acl: { module: 'CARD_KYC_TRACKING', action: 'VIEW:CARD_KYC_TRACKING' } },
        { label: 'Issuer Card KYC', path: '/cms/issuer-card-kyc', acl: { module: 'ISSUER_CARD_KYC', action: 'VIEW:ISSUER_CARD_KYC' } },
        { label: 'Name Check', path: '/cms/card-name-check', acl: { module: 'CARD_NAME_CHECK', action: 'VIEW:CARD_NAME_CHECK' } },
        { label: 'OOB Verification', path: '/cms/card-oob-delivery', acl: { module: 'CMS', action: 'VIEW:CMS_OOB_NOTIFICATION_LOG' } },
        { label: 'Code Delivery', path: '/cms/notification-log', acl: { module: 'CMS', action: 'VIEW:CMS_NOTIFICATION_LOG' } },
      ],
    },
    {
      label: 'Disputes & Config',
      path: '/cms/card-disputes',
      children: [
        { label: 'Card Disputes', path: '/cms/card-disputes', acl: { module: 'CMS', action: 'VIEW:CMS_CARD_DISPUTES' } },
        {
          label: 'Card Force Posting',
          path: '/cms/force-posting',
          acl: { module: 'CMS', action: 'VIEW:CMS_FORCE_POSTING' },
        },
        { label: 'Global LTV Config', path: '/cms/asset-value-config', acl: { module: 'CMS', action: 'VIEW:ASSET_VALUE_CONFIG' } },
        { label: 'Tribe TAI Webhooks', path: '/cms/tribe-tai-webhooks', acl: { module: 'TRIBE_TAI', action: 'VIEW:TRIBE_TAI_NOTIFICATION' }, superadminOnly: true },
      ],
    },
  ],
};

/**
 * Products domain - Financial products
 */
const productsDomain: MegaMenuDomain = {
  id: 'products',
  title: 'Products',
  icon: 'AppstoreOutlined',
  items: [
    {
      label: 'Digital Assets',
      path: '/digital-custody/deposits',
      children: [
        { label: 'Deposits', path: '/digital-custody/deposits', acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_DEPOSIT' } },
        { label: 'Withdrawals', path: '/digital-custody/withdraws', acl: { module: 'DIGITAL', action: 'VIEW:DIGITAL_WITHDRAW' } },
        { label: 'Address Screening', path: '/digital-custody/address-screening', acl: { module: 'DIGITAL', action: 'VIEW:*' } },
      ],
    },
    {
      label: 'Earn',
      path: '/earn/offers',
      children: [
        { label: 'Offers', path: '/earn/offers', acl: { module: 'EARN', action: 'VIEW:EARN_OFFER' } },
        { label: 'Subscriptions', path: '/earn/subscriptions', acl: { module: 'EARN', action: 'VIEW:EARN_SUBSCRIPTION' } },
        { label: 'Positions', path: '/earn/position', acl: { module: 'EARN', action: 'VIEW:EARN_POSITION' } },
        { label: 'Providers', path: '/earn/providers', acl: { module: 'EARN', action: 'VIEW:EARN_PROVIDER' } },
      ],
    },
    {
      label: 'Securities',
      path: '/securities/brokers',
      children: [
        { label: 'Brokers', path: '/securities/brokers', acl: { module: 'SECURITIES', action: 'VIEW:SECURITIES' } },
        { label: 'Accounts', path: '/securities/accounts', acl: { module: 'SECURITIES', action: 'VIEW:SECURITIES_ACCOUNTS_LIST' } },
      ],
    },
    {
      label: 'Fund',
      path: '/fund/positions',
      children: [
        { label: 'Positions', path: '/fund/positions', acl: { module: 'IFAST_FUND', action: 'VIEW:IFAST_POSITION_LIST' } },
        { label: 'Instructions', path: '/fund/instructions', acl: { module: 'IFAST_FUND', action: 'VIEW:IFAST_INSTRUCTION_LIST' } },
        { label: 'iFast Accounts', path: '/fund/ifast-accounts', acl: { module: 'IFAST_FUND', action: 'VIEW:IFAST_ACCOUNT_LIST' } },
      ],
    },
    {
      label: 'Precious Metals',
      path: '/precious-metal/products',
      children: [
        { label: 'Products', path: '/precious-metal/products', acl: { module: 'PRECIOUS_METAL', action: 'VIEW:PRECIOUS_METAL_PRODUCT' } },
        { label: 'Instructions', path: '/precious-metal/instructions', acl: { module: 'PRECIOUS_METAL', action: 'VIEW:PRECIOUS_METAL_INSTRUCTION' } },
        { label: 'Positions', path: '/precious-metal/positions', acl: { module: 'PRECIOUS_METAL', action: 'VIEW:PRECIOUS_METAL_POSITION' } },
      ],
    },
    {
      label: 'Other',
      path: '/alternatives',
      children: [
        { label: 'Alternatives', path: '/alternatives', acl: { module: 'ALTERNATIVES', action: 'VIEW:ALTERNATIVES' } },
        { label: 'Insurance Policies', path: '/insurance/policies', acl: { module: 'INSURANCE', action: 'VIEW:INSURANCE' } },
        { label: 'Insurance Companies', path: '/insurance/companies', acl: { module: 'INSURANCE', action: 'VIEW:INSURANCE_COMPANY' } },
        { label: 'NFTs', path: '/nfts', acl: { module: 'NFT', action: 'VIEW:NFT' } },
      ],
    },
  ],
};

/**
 * Compliance domain - Cases, AML, Fraud
 */
const complianceDomain: MegaMenuDomain = {
  id: 'compliance',
  title: 'Compliance',
  icon: 'SafetyCertificateOutlined',
  items: [
    {
      label: 'Cases',
      path: '/compliance/cases',
      children: [
        { label: 'All Cases', path: '/compliance/cases', acl: { module: 'COMPLIANCE', action: 'VIEW:COMPLIANCE_CASE' } },
        { label: 'Case Rules', path: '/compliance/cases/rules', acl: { module: 'COMPLIANCE', action: 'ACTION:MANAGE_COMPLIANCE_RULES' } },
      ],
    },
    {
      label: 'Screening',
      path: '/name-check',
      children: [
        { label: 'Name Check', path: '/name-check', acl: { module: 'NAME_CHECK', action: 'VIEW:NAME_CHECK' } },
        { label: 'WorldCheck Settings', path: '/name-check/settings', acl: { module: 'NAME_CHECK', action: 'ACTION:CONFIGURE_NAME_CHECK_THRESHOLDS' } },
        { label: 'KYT Screening', path: '/compliance/kyt-screening', acl: { module: 'DIGITAL', action: 'VIEW:*' } },
        { label: 'Batch Rescreen', path: '/digital-custody/batch-rescreen', acl: { module: 'DIGITAL', action: 'VIEW:*' } },
        { label: 'Screening History', path: '/digital-custody/screening-history', acl: { module: 'DIGITAL', action: 'VIEW:*' } },
      ],
    },
    {
      label: 'Fraud',
      path: '/transaction-monitoring/fraud-transactions',
      children: [
        { label: 'Fraud Transactions', path: '/transaction-monitoring/fraud-transactions', acl: { module: 'CARD_FRAUD_SYSTEM', action: 'VIEW:CARD_FRAUD_SYSTEM' } },
        { label: 'Card Fraud Rules', path: '/transaction-monitoring/card-fraud-rules-configuration', acl: { module: 'CARD_FRAUD_SYSTEM', action: 'VIEW:CARD_FRAUD_SYSTEM' } },
        { label: 'Blacklist Merchants', path: '/transaction-monitoring/blacklist-merchants', acl: { module: 'BLACKLIST_MERCHANT', action: 'VIEW:BLACKLIST_MERCHANT' } },
        { label: 'Whitelist Merchants', path: '/transaction-monitoring/whitelist-merchants', acl: { module: 'CARD_MERCHANT_WHITELIST', action: 'VIEW:CARD_MERCHANT_WHITELIST' } },
      ],
    },
    {
      label: 'Transaction Monitoring',
      path: '/flow-analysis',
      children: [
        { label: 'Flow Analysis', path: '/flow-analysis', acl: { module: 'USER', action: 'VIEW:USER' } },
      ],
    },
    {
      label: 'Client Consent',
      path: '/client-consent',
      children: [
        { label: 'Consent Documents', path: '/client-consent', acl: { module: 'CLIENT_CONSENT', action: 'VIEW:CLIENT_CONSENT' } },
        {
          label: 'Professional Investor',
          path: '/client-consent/professional-investor',
          acl: { module: 'PI_QUALIFICATION', action: 'VIEW:PI_QUALIFICATION' }
        }
      ],
    },
  ],
};

/**
 * Back Office domain - Finance, Admin, Config
 */
const backOfficeDomain: MegaMenuDomain = {
  id: 'backoffice',
  title: 'Back Office',
  icon: 'SettingOutlined',
  items: [
    {
      label: 'Reports',
      path: '/reporting/standard',
      children: [
        { label: 'Standard Reports', path: '/reporting/standard', acl: { module: 'REPORTING', action: 'VIEW:REPORTING' } },
        { label: 'Accounting Reports', path: '/reporting/accounting', acl: { module: 'ACCOUNTING_REPORT', action: 'VIEW:ACCOUNTING_REPORT' } },
        { label: 'Cobrander P&L', path: '/reporting/cobrander-pnl', acl: { module: 'ACCOUNTING', action: 'VIEW:COBRANDER_PNL_REPORT' } },
        { label: 'Journal Reports', path: '/reporting/journal', acl: { module: 'JOURNAL_REPORT', action: 'VIEW:JOURNAL_REPORT' } },
        { label: 'Report History', path: '/reporting/history', acl: { module: 'REPORTING_HISTORY', action: 'VIEW:REPORTING_HISTORY' } },
        { label: 'Report Configurations', path: '/reporting/configurations', acl: { module: 'REPORTING', action: 'VIEW:REPORTING' } },
        { label: 'Sensitive Card Report', path: '/reporting/sensitive-card', acl: { module: 'SENSITIVE_CARD_REPORT', action: 'ACTION:DOWNLOAD_SENSITIVE_CARD_REPORT' } },
      ],
    },
    {
      label: 'Ledger',
      path: '/accounting/journal-entries',
      children: [
        { label: 'Journal Entries', path: '/accounting/journal-entries', acl: { module: 'JOURNAL_ENTRIES', action: 'VIEW:JOURNAL_ENTRIES' } },
        { label: 'Book Entries', path: '/accounting/book-entries', acl: { module: 'BOOK_ENTRIES', action: 'VIEW:BOOK_ENTRIES' } },
        { label: 'Ledger Accounts', path: '/accounting/ledger-account', acl: { module: 'LEDGER_ACCOUNT', action: 'VIEW:LEDGER_ACCOUNT' } },
        { label: 'Accounting Tags', path: '/accounting/tags', acl: { module: 'ACCOUNTING_TAG', action: 'VIEW:ACCOUNTING_TAG' } },
      ],
    },
    {
      label: 'Finance',
      path: '/accounting/balance-sheet',
      children: [
        { label: 'Balance Sheet', path: '/accounting/balance-sheet', acl: { module: 'ACCOUNTING', action: 'VIEW:BALANCE_SHEET' } },
        { label: 'Bank Available Balance', path: '/accounting/bank-available-balance', acl: { module: 'BANK_AVAILABLE_BALANCE', action: 'VIEW:BANK_AVAILABLE_BALANCE' } },
        { label: 'Operational Accounts', path: '/accounting/operational-accounts', acl: { module: 'ACCOUNTING', action: 'VIEW:OPERATIONAL_ACCOUNTS' } },
        { label: 'Client Asset Overview', path: '/accounting/unigroup-client-asset-balance', acl: { module: 'ACCOUNTING', action: 'VIEW:UNIGROUP_CLIENT_ASSET_BALANCE' } },
        { label: 'UniGroup Book Management', path: '/accounting/unigroup-book-management', acl: { module: 'UNIGROUP_BOOK_MANAGEMENT', action: 'VIEW:UNIGROUP_BOOK_MANAGEMENT' } },
      ],
    },
    {
      label: 'Revenue',
      path: '/accounting/commission-details',
      children: [
        { label: 'Invoices', path: '/invoices', acl: { module: 'CLIENT_DOCUMENT', action: 'VIEW:INVOICE' } },
        { label: 'Commission Detail', path: '/accounting/commission-details', acl: { module: 'COMMISSION_DETAILS', action: 'VIEW:COMMISSION_DETAILS' } },
        { label: 'Sales Revenue Summary', path: '/accounting/sales-revenue-summary', acl: { module: 'SALES_REVENUE_SUMMARY', action: 'VIEW:SALES_REVENUE_SUMMARY' } },
        { label: 'Sales Team Revenue Statistics', path: '/accounting/sales-revenue-statistics', acl: { module: 'SALES_REVENUE_SUMMARY', action: 'VIEW:SALES_REVENUE_SUMMARY' } },
        { label: 'Revenue Breakdown', path: '/accounting/revenue-breakdown', acl: { module: 'REVENUE_BREAKDOWN', action: 'VIEW:REVENUE_BREAKDOWN' } },
      ],
    },
    {
      label: 'Fees & Pricing',
      path: '/cost-and-fees/cost-management',
      children: [
        { label: 'Cost Management', path: '/cost-and-fees/cost-management', acl: { module: 'REVENUE', action: 'VIEW:FEE_COST' } },
        { label: 'Markup Management', path: '/cost-and-fees/fee-markup-management', acl: { module: 'REVENUE', action: 'VIEW:FEE_MARKUP' } },
        { label: 'Fee Schedule', path: '/cost-and-fees/fee-schedule', acl: { module: 'REVENUE', action: 'VIEW:FEE_SCHEDULE' } },
        { label: 'New Fee Schedule', path: '/cost-and-fees/fee-schedule-v2', acl: { module: 'REVENUE', action: 'VIEW:FEE_SCHEDULE' } },
        { label: 'Users Fee Schedule', path: '/cost-and-fees/users-fee-schedule', acl: { module: 'REVENUE', action: 'VIEW:FEE_SCHEDULE' } },
        { label: 'Cobrands Fee Schedule', path: '/cost-and-fees/cobrands-fee-schedule', acl: { module: 'REVENUE', action: 'VIEW:COBRAND_FEE_SCHEDULE' } },
        { label: 'Commission Management', path: '/cost-and-fees/commission-management', acl: { module: 'REVENUE', action: 'VIEW:COMMISSION_MANAGEMENT' } },
        { label: 'Fee Setting Request', path: '/cost-and-fees/fee-setting-request', acl: { module: 'REVENUE', action: 'VIEW:FEE_SETTING_PREVIEW' } },
        { label: 'Agent Onboarding', path: '/cost-and-fees/agent-onboarding', acl: { module: 'AGENT_ONBOARDING', action: 'VIEW:AGENT_ONBOARDING' } },
      ],
    },
    {
      label: 'Rates',
      path: '/rate-source/configuration',
      children: [
        { label: 'Configuration', path: '/rate-source/configuration', acl: { module: 'RATE_SOURCE', action: 'VIEW:RATE_SOURCE' } },
        { label: 'Rate Reference', path: '/rate-source/rate-reference', acl: { module: 'REVENUE', action: 'VIEW:RATE_REFERENCE' } },
        { label: 'Rateboard', path: '/rate-source/rate-board', acl: { module: 'RATE_SOURCE', action: 'VIEW:RATE_BOARD' } },
      ],
    },
    {
      label: 'Admin',
      path: '/super/operators',
      children: [
        { label: 'Operators', path: '/super/operators', acl: { module: 'OPERATOR', action: 'VIEW:OPERATOR' } },
        { label: 'Audit Logs', path: '/audit-logs', acl: { module: 'AUDIT_LOG', action: 'VIEW:AUDIT_LOG' } },
        { label: 'Developer Console', path: '/developer-console', acl: { module: 'DEVELOPER', action: 'ACTION:DEVELOPER_IMPERSONATE_PREVIEW' } },
        { label: 'Tag Management', path: '/tag-management', acl: { module: 'TAG_MANAGEMENT', action: 'VIEW:TAG_MANAGEMENT' } },
      ],
    },
    {
      label: 'Operations',
      path: '/cash-inventory',
      children: [
        { label: 'Cash Inventory', path: '/cash-inventory', acl: { module: 'TREASURY', action: 'VIEW:CASH_INVENTORY' } },
        { label: 'Purchase Orders', path: '/purchase-order', acl: { module: 'PURCHASE_ORDER', action: 'VIEW:PURCHASE_ORDER' } },
        { label: 'Digital Asset Treasury', path: '/digital-asset-treasury', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' } },
        { label: 'Transfer Requests', path: '/digital-asset-treasury/requests', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' } },
        { label: 'Transfer Tasks', path: '/digital-asset-treasury/tasks', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' } },
        { label: 'TRON Delegations', path: '/digital-asset-treasury/tron-delegations', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:TRON_DELEGATIONS' } },
        { label: 'Automation Rules', path: '/digital-asset-treasury/automation', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' } },
        { label: 'Treasury Balances', path: '/digital-asset-treasury/treasury-balances', acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' } },
      ],
    },
  ],
};

/**
 * Support Center domain - Help and feedback
 */
const supportCenterDomain: MegaMenuDomain = {
  id: 'support',
  title: 'Support Center',
  icon: 'CustomerServiceOutlined',
  items: [
    {
      label: 'Requests',
      path: '/support-center/all-requests',
      children: [
        { label: 'All Requests', path: '/support-center/all-requests', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER' } },
        { label: 'My Requests', path: '/support-center/my-requests', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_MY_REQUEST' } },
        { label: 'Team Board', path: '/support-center/team-board', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_TEAM_BOARD' } },
      ],
    },
    {
      label: 'Submit',
      path: '/support-center/new',
      children: [
        { label: 'Report Issue', path: '/support-center/report-issue', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_MY_REQUEST' } },
        { label: 'Client Issue', path: '/support-center/client-issue', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_CLIENT_ISSUE' } },
        { label: 'Feature Request', path: '/support-center/feature-request', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_MY_REQUEST' } },
        { label: 'Give Feedback', path: '/support-center/feedback', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:SUPPORT_CENTER_MY_REQUEST' } },
      ],
    },
    {
      label: 'Admin',
      path: '/support-center/category-admin',
      children: [
        { label: 'Category Management', path: '/support-center/category-admin', acl: { module: 'SUPPORT_CENTER', action: 'ACTION:MANAGE_ISSUE_CATEGORIES' } },
        { label: 'Ticket Tags', path: '/support-center/tag-admin' },
        { label: 'Category Analytics', path: '/support-center/category-analytics', acl: { module: 'SUPPORT_CENTER', action: 'VIEW:ISSUE_CATEGORY_ANALYTICS' } },
      ],
    },
  ],
};

/**
 * System domain - DevOps and infrastructure configuration
 */
const systemDomain: MegaMenuDomain = {
  id: 'system',
  title: 'System',
  icon: 'ToolOutlined',
  items: [
    {
      label: 'Frontend App',
      path: '/frontend-app',
      children: [
        { label: 'App disclosures', path: '/app-disclosures', acl: { module: 'APP_DISCLOSURE', action: 'VIEW:APP_DISCLOSURE' } },
        { label: "App What's New", path: '/app-whats-new', acl: { module: 'APP_WHATS_NEW', action: 'VIEW:APP_WHATS_NEW' } },
        { label: 'Important Notices', path: '/app-important-notices', acl: { module: 'APP_IMPORTANT_NOTICES', action: 'VIEW:APP_IMPORTANT_NOTICES' } },
        { label: 'Card Offers', path: '/app-card-offers', acl: { module: 'APP_CARD_OFFERS', action: 'VIEW:APP_CARD_OFFERS' } }
      ]
    },
    {
      label: 'Vendor Configuration',
      path: '/vendor-config',
      children: [
        { label: 'Settings', path: '/vendor-config', acl: { module: 'VENDOR_CONFIG', action: 'VIEW:VENDOR_CONFIG' } },
        {
          label: 'Digital Asset Treasury',
          path: '/vendor-config?tab=digital-asset-treasury',
          acl: { module: 'DIGITAL_ASSET_TREASURY', action: 'VIEW:DIGITAL_ASSET_TREASURY' }
        }
      ]
    },
    {
      label: 'Customer Support',
      path: '/system/cs-telegram-bot',
      children: [
        { label: 'CS Telegram Bot', path: '/system/cs-telegram-bot', acl: { module: 'CS_TELEGRAM_BOT', action: 'VIEW:CS_TELEGRAM_BOT' } }
      ]
    },
    {
      label: 'Infrastructure',
      path: '/system/config',
      children: [
        { label: 'Config & Secrets', path: '/system/config', acl: { module: 'VENDOR_CONFIG', action: 'VIEW:VENDOR_CONFIG' } },
        { label: 'Feature Flags', path: '/system/feature-flags', acl: { module: 'FEATURE_FLAG_RULES', action: 'VIEW:FEATURE_FLAG_RULES' }, superadminOnly: true },
        { label: 'Feature Readiness', path: '/system/feature-readiness', acl: { module: 'FEATURE_READINESS', action: 'VIEW:FEATURE_READINESS' } },
        { label: 'System Notice Emails', path: '/system/system-notice-emails', acl: { module: 'SYSTEM_NOTICE_EMAIL', action: 'VIEW:SYSTEM_NOTICE_EMAIL' } }
      ]
    },
  ],
};

/**
 * Menu structure: Row 1 (4 columns), Row 2 (5 columns)
 */
export const megaMenuConfig: MegaMenuRow[] = [
  {
    domains: [workDomain, customersDomain, instructionsDomain, cardsDomain],
  },
  {
    domains: [productsDomain, complianceDomain, backOfficeDomain, supportCenterDomain, systemDomain],
  },
];

/**
 * Get all domains as a flat array
 */
export const getAllDomains = (): MegaMenuDomain[] => {
  return megaMenuConfig.flatMap((row) => row.domains);
};

/**
 * Get a domain by ID
 */
export const getDomainById = (id: string): MegaMenuDomain | undefined => {
  return getAllDomains().find((domain) => domain.id === id);
};

/**
 * Flattened menu item for search/command palette
 */
export interface FlatMenuItem {
  id: string;
  label: string;
  path: string;
  domain: string;
  domainId: string;
  parentLabel?: string;
  acl?: { module: string; action: string };
  badgeKey?: string;
}

/**
 * Flatten all menu items for search functionality
 * Returns all navigable items with their domain and parent context
 */
export function flattenMenuItems(): FlatMenuItem[] {
  const items: FlatMenuItem[] = [];
  for (const domain of getAllDomains()) {
    for (const item of domain.items) {
      if (item.children?.length) {
        // Add children as individual items
        for (const child of item.children) {
          items.push({
            id: `${domain.id}-${child.path}`,
            label: child.label,
            path: child.path,
            domain: domain.title,
            domainId: domain.id,
            parentLabel: item.label,
            acl: child.acl,
            badgeKey: child.badgeKey,
          });
        }
      } else {
        // Add top-level item (no children)
        items.push({
          id: `${domain.id}-${item.path}`,
          label: item.label,
          path: item.path,
          domain: domain.title,
          domainId: domain.id,
          acl: item.acl,
          badgeKey: item.badgeKey,
        });
      }
    }
  }
  return items;
}
